import React, { Component, ErrorInfo, ReactNode, forwardRef } from "react";
import { CASA_API_ERROR_MESSAGE } from "@/constant";
import { captureException } from "@/services/sentry";
import { getCachedFingerprint } from "@/utils";

interface ErrorRecord {
  timestamp: number;
  error: Error;
  errorInfo: ErrorInfo;
}

interface EnhancedErrorBoundaryProps {
  children: ReactNode;
  timeWindow?: number; // 时间窗口（毫秒），默认 2 分钟
  errorThreshold?: number; // 触发阈值，默认 3 次
  onThresholdReached?: (errors: ErrorRecord[]) => void; // 达到阈值时的回调函数
  disableOnThreshold?: boolean; // 是否在达到阈值后禁用组件
  onAsyncError?: (error: Error) => void; // 异步错误处理回调
}

interface EnhancedErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorHistory: ErrorRecord[];
  isDisabled: boolean;
}

class EnhancedErrorBoundary extends Component<
  EnhancedErrorBoundaryProps,
  EnhancedErrorBoundaryState
> {
  private timeWindow: number;
  private errorThreshold: number;
  private cleanupTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: EnhancedErrorBoundaryProps) {
    super(props);

    this.timeWindow = props.timeWindow || 2 * 60 * 1000; // 默认 2 分钟
    this.errorThreshold = props.errorThreshold || 3; // 默认 3 次

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorHistory: [],
      isDisabled: false,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<EnhancedErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.beforeHandleError(error, errorInfo);
  }

  private shouldHandleError = (error: Error) => {
    if (
      error instanceof Error &&
      typeof error.message === "string" &&
      error.message.includes(CASA_API_ERROR_MESSAGE)
    ) {
      return true;
    }
    return false;
  };

  private beforeHandleError = (error: Error, errorInfo?: ErrorInfo) => {
    if (!this.shouldHandleError(error)) {
      return;
    }
    this.handleError(error, errorInfo);
    // 记录错误到 Sentry 和控制台
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    captureException(error, {
      extra: {
        fingerprint: getCachedFingerprint(),
      },
    });
  };

  // 处理错误的通用方法，可被同步和异步错误调用
  private handleError = (error: Error, errorInfo?: ErrorInfo) => {
    const now = Date.now();
    const newErrorRecord: ErrorRecord = {
      timestamp: now,
      error,
      errorInfo: errorInfo || { componentStack: "" },
    };

    // 清理过期的错误记录
    const validErrors = this.state.errorHistory.filter(
      (record) => now - record.timestamp <= this.timeWindow
    );

    // 添加新的错误记录
    const updatedErrorHistory = [...validErrors, newErrorRecord];

    this.setState({
      hasError: true,
      error,
      errorInfo: errorInfo || null,
      errorHistory: updatedErrorHistory,
    });

    // 检查是否达到阈值
    if (updatedErrorHistory.length >= this.errorThreshold) {
      this.handleThresholdReached(updatedErrorHistory);
    }

    this.scheduleCleanup();

    // 如果有异步错误处理回调，则调用
    if (this.props.onAsyncError) {
      this.props.onAsyncError(error);
    }
  };

  private handleThresholdReached = (errors: ErrorRecord[]) => {
    console.warn(
      `ErrorBoundary: ${this.errorThreshold} errors occurred within ${
        this.timeWindow / 1000
      } seconds`
    );

    // 执行回调函数
    if (this.props.onThresholdReached) {
      this.props.onThresholdReached(errors);
    }

    // 如果设置了禁用选项，则禁用组件
    if (this.props.disableOnThreshold) {
      this.setState({ isDisabled: true });
    }
  };

  private scheduleCleanup = () => {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
    }

    this.cleanupTimer = setTimeout(() => {
      const now = Date.now();
      const validErrors = this.state.errorHistory.filter(
        (record) => now - record.timestamp <= this.timeWindow
      );

      if (validErrors.length !== this.state.errorHistory.length) {
        this.setState({ errorHistory: validErrors });
      }
    }, this.timeWindow);
  };

  componentDidMount() {
    // 全局错误处理器，用于捕获未被处理的 Promise 拒绝和运行时错误
    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  componentWillUnmount() {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
    }
    // 清理全局错误监听器
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  // 处理全局错误
  private handleGlobalError = (event: ErrorEvent) => {
    try {
      const error = event.error || new Error(event.message);
      this.beforeHandleError(error);
    } catch (e) {
      // noop
    }
  };

  // 处理未捕获的 Promise 拒绝
  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    try {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      this.beforeHandleError(error);
    } catch (e) {
      // noop
    }
  };

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isDisabled: false,
    });
  };

  render() {
    const { hasError, isDisabled, error } = this.state;
    const { children } = this.props;

    // 如果组件被禁用
    if (isDisabled) {
      return null;
    }

    return children;
  }
}

// 导出类组件，支持 ref
export default EnhancedErrorBoundary;

// 也可以创建一个 forwardRef 版本（可选）
export const EnhancedErrorBoundaryWithRef = forwardRef<
  EnhancedErrorBoundary,
  EnhancedErrorBoundaryProps
>((props, ref) => <EnhancedErrorBoundary {...props} ref={ref} />);
